import SparkMD5 from 'spark-md5';
const checkPDFEncryptionStatus = async fileReader => {
  const fileText = await new Blob([fileReader.result], {
    type: 'application/pdf'
  }).text();
  return !!(fileText.includes('Encrypt') || fileText.substring(fileText.lastIndexOf('<<'), fileText.lastIndexOf('>>')).includes('/Encrypt'));
};
const identifyFileTypeFromMagicNumber = async (fileReader, header) => {
  const fileProperties = {};
  switch (header) {
    case '89504e47':
      fileProperties.mimeType = 'image/png';
      break;
    case 'ffd8ffe0':
    case 'ffd8ffe1':
    case 'ffd8ffe2':
    case 'ffd8ffe3':
    case 'ffd8ffe8':
      fileProperties.mimeType = 'image/jpeg';
      break;
    case '47494638':
      fileProperties.mimeType = 'image/gif';
      break;
    case '4d4d002a':
    case '49492a00':
      fileProperties.mimeType = 'image/tiff';
      break;
    case '25504446':
      fileProperties.mimeType = 'application/pdf';
      fileProperties.encrypted = await checkPDFEncryptionStatus(fileReader);
      break;
    default:
      fileProperties.mimeType = undefined;
      break;
  }
  return fileProperties;
};
const validateFileByTypeAndEncryption = async (fileReader, validMimeTypes) => {
  const arr = new Uint8Array(fileReader.result).subarray(0, 4);
  let header = '';
  for (let i = 0; i < arr.length; i += 1) {
    header += arr[i].toString(16);
  }
  const _await$identifyFileTy = await identifyFileTypeFromMagicNumber(fileReader, header),
    mimeType = _await$identifyFileTy.mimeType,
    _await$identifyFileTy2 = _await$identifyFileTy.encrypted,
    encrypted = _await$identifyFileTy2 === void 0 ? false : _await$identifyFileTy2;
  const validMessage = "Only files of type \"".concat(validMimeTypes.join('", "'), "\" are allowed.");
  return {
    isValid: validMimeTypes.includes(mimeType) && !encrypted,
    message: "File is of type \"".concat(mimeType, "\"").concat(encrypted ? ' and is encrypted.' : '', "\n").concat(validMessage)
  };
};

/**
   * Adapted from hash a file incrementally example
   * https://github.com/satazor/js-spark-md5
   */
const detectFileAndCreateMD5Hash = function detectFileAndCreateMD5Hash(file) {
  let validTypes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ['application/pdf'];
  return new Promise((resolve, reject) => {
    if (typeof file === 'undefined') {
      reject(new Error('File argument is required.'));
    }
    const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
    const chunkSize = 2097152; // Read in chunks of 2MB
    const maxSize = 2097152000; // Max Size is 2GB
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();

    // FileReader is a defined global - lint has trouble
    /* eslint-disable-next-line no-undef */
    const fileReader = new FileReader();
    const loadChunk = () => {
      if (file.size > maxSize) {
        return reject(new Error('Filesize may not exceed 2GB.'));
      }
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize;
      return fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    };
    fileReader.onload = async e => {
      currentChunk += 1;
      spark.append(e.target.result); // Append array buffer

      // only run file detection on the first chunk,
      // that's the one that is prefixed with the magic number
      if (currentChunk === 1) {
        const _await$validateFileBy = await validateFileByTypeAndEncryption(fileReader, validTypes),
          isValid = _await$validateFileBy.isValid,
          message = _await$validateFileBy.message;

        // if file has been deemed invalid, reject promise and return error message
        if (!isValid) return reject(message);
      }

      // if there are more chunks to process, keep processing
      if (currentChunk < chunks) return loadChunk();

      // if this is the last chunk, end the spark buffer and harvest the result
      const md5 = spark.end();

      // if file passes validation, return md5 hash
      return resolve(md5);
    };
    fileReader.onerror = error => reject(error);
    loadChunk();
  });
};
export default {
  detectFileAndCreateMD5Hash
};